import {
  getHeaderImage,
  PINWHEEL_LOGO_DARK,
  getContrastingBrandliveLogo,
  FOOTER_CARD_STYLE,
  getSessionDateLabel,
  getSessionTimeLabel,
  resolveThemedColor,
  resolveThemedFont,
  resolveThemedRadius,
  formatDateParts,
} from './utils/utils';
import { ECustomDesignTypes, EPaletteModes } from '@/types/theme-packs.types';
import type { TemplateProps } from './campaign-template.types';
import ContentEditable from './content-editable';
import styles from './template-styles.module.css';
import HeaderWidget from './widgets/header-widget';
import ImageWidget from './widgets/image-widget';
import ParagraphWidget from './widgets/paragraph-widget';
import FooterWidget from './widgets/footer-widget';
import LogoWidget from './widgets/logo-widget';
import { templateStaticDefaults } from './template-defaults';
import { BLOCK_ID } from './widgets/block-ids';
import { DateTimeLayout } from './widgets/date-time-layout';
import { BlockRenderer } from './widgets/block-renderer';
import BlockShell from '../editor/components/block-shell';
import CalendarLinksWidget from './widgets/calendar-links-widget';

const DEFAULTS = templateStaticDefaults.aurelia;

const AURELIA_BLOCK_PRESETS: Record<string, Record<string, unknown>> = {
  [BLOCK_ID.headerLogo]: { height: 40, padding: '24px' },
  [BLOCK_ID.badge]: {
    fitContent: true,
    color: '#17181C',
    fontFamily: 'var(--heading-font)',
    fontSize: '14px',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    background: 'var(--secondary-accent)',
    borderRadius: '100px',
    padding: '8px 16px',
    margin: '0 56px 20px 56px',
  },
  [BLOCK_ID.salutation]: {
    color: 'var(--heading)',
    fontFamily: 'var(--heading-font)',
    fontSize: '80px',
    fontWeight: 700,
    lineHeight: '68px',
    whiteSpace: 'pre-line',
    padding: '0 56px 40px 56px',
  },
  [BLOCK_ID.mainImage]: { width: 600, height: 400, borderRadius: 'var(--image-radius)' },
  [BLOCK_ID.description]: {
    color: '#FFFFFF',
    fontFamily: 'var(--body-font)',
    fontSize: '24px',
    fontWeight: 700,
    lineHeight: '29px',
    padding: '48px 56px 32px 56px',
  },
  [BLOCK_ID.cta]: {
    fullWidth: true,
    height: '56px',
    buttonPadding: '0',
    color: 'var(--button-text)',
    buttonColor: 'var(--button-color)',
    fontFamily: 'var(--heading-font)',
    fontWeight: 700,
    fontSize: '18px',
    padding: '20px 56px',
  },
  [BLOCK_ID.legalText]: {
    color: '#FFEAED',
    fontFamily: 'var(--body-font)',
    fontSize: '11px',
    fontWeight: 400,
    lineHeight: '17px',
    opacity: 0.8,
    padding: '56px 56px 40px 56px',
  },
  // the date & time block keeps aurelia's original black stacked card
  [BLOCK_ID.dateTimeCard]: {
    layout: DateTimeLayout.Stacked,
    padding: '12px 56px',
    cardBackground: '#000000',
    cardBorderRadius: '4px',
    borderStyle: 'none',
    dateRowPadding: '32px',
    groupGap: '24px',
    timeHeading: DEFAULTS.startTimeLabel,
    labelStyle: {
      color: 'var(--body)',
      opacity: 0.6,
      fontSize: '12px',
      fontWeight: 700,
      marginBottom: '4px',
    },
    valueStyle: { color: 'var(--text-on-dark-fill)', fontSize: '20px' },
  },
  [BLOCK_ID.mailingAddress]: { ...FOOTER_CARD_STYLE, fontFamily: 'var(--body-font)' },
};

export const Aurelia = ({
  event,
  theme,
  mode,
  timezone,
  href,
  calendarLinks,
  customizations,
  salutation,
  setSalutation,
  description,
  setDescription,
  cta,
  setCta,
  setMainImage,
  setHeaderImage,
  blocks,
  onUpdateBlockValue,
  onUpdateBlockImage,
  onDeleteBlock,
  onMoveBlock,
  selectedBlockId,
  onSelectBlock,
  isEditMode,
  hideBrandliveLogo,
  privacyPolicyLabel,
}: TemplateProps) => {
  const resolvedMode = mode ?? theme?.theme_mode ?? EPaletteModes.Light;
  const themeColors = theme?.colors?.[resolvedMode] ?? theme?.colors;
  const bg = resolveThemedColor(
    customizations?.backgroundColor,
    themeColors?.backgroundColor,
    DEFAULTS.backgroundColor
  );
  const container = resolveThemedColor(
    customizations?.containerColor,
    themeColors?.containerColor,
    DEFAULTS.containerColor
  );
  const accent = resolveThemedColor(customizations?.accentColor, themeColors?.accentColor, DEFAULTS.accentColor);
  const secondaryAccent = resolveThemedColor(
    customizations?.secondaryAccentColor,
    themeColors?.secondaryAccentColor,
    DEFAULTS.secondaryAccentColor
  );
  const heading = resolveThemedColor(
    customizations?.headingTextColor,
    themeColors?.headingTextColor,
    DEFAULTS.headingTextColor
  );
  const textOnDarkFill = '#FFFFFF';
  const buttonText = resolveThemedColor(
    customizations?.buttonTextColor,
    themeColors?.buttonTextColor,
    DEFAULTS.buttonTextColor ?? accent
  );
  const body = resolveThemedColor(customizations?.bodyTextColor, themeColors?.bodyTextColor, DEFAULTS.bodyTextColor);
  const buttonColor = DEFAULTS.buttonColor ?? accent;
  const buttonRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.button], '4px');
  const containerRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.container], '0px');
  const imageRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.img], '0px');
  const sessionTimestamp = event.sessions[0]?.timestamp;
  const sessionDateLabel = getSessionDateLabel(
    sessionTimestamp,
    timezone,
    {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    },
    { dayFirst: true, commaBeforeYear: true }
  );
  const sessionTimeLabel = getSessionTimeLabel(sessionTimestamp, timezone);
  const sessionDateParts = sessionTimestamp ? formatDateParts(sessionTimestamp, timezone) : null;
  const anchorHref = href || '#';
  const headerImg = customizations?.headingImage || getHeaderImage(event) || PINWHEEL_LOGO_DARK;
  const bodyImg = customizations?.mainImage || DEFAULTS.image;
  const headingFont = resolveThemedFont(
    customizations?.headingFont,
    theme?.font_pack?.heading_name,
    '"Archivo Black", Arial Black, sans-serif'
  );
  const bodyFont = resolveThemedFont(
    customizations?.bodyFont,
    theme?.font_pack?.body_name,
    'Archivo, Arial, sans-serif'
  );
  const _salutation = salutation ?? (customizations?.salutation || DEFAULTS.salutation);
  const eventDescription = description || customizations?.description || DEFAULTS.description;
  const _cta = cta ?? (customizations?.cta || DEFAULTS.cta);

  const bodyColor = container;
  const footerTextColor = body;
  const footerBackground = bg;
  const brandliveLogo = hideBrandliveLogo ? undefined : getContrastingBrandliveLogo(bodyColor);

  if (blocks?.length) {
    const theme = {
      bg,
      container,
      accent,
      buttonColor,
      secondaryAccent,
      heading,
      buttonText,
      body,
      headingFont,
      bodyFont,
      imageRadius,
      buttonRadius,
      containerRadius,
      bodyBg: bodyColor,
      footerText: footerTextColor,
      footerBg: footerBackground,
    };
    const blockContext = {
      blockPresets: AURELIA_BLOCK_PRESETS,
      brandliveLogo,
      privacyPolicyLabel,
      href: anchorHref,
      theme,
      dateLabel: timezone ? sessionDateLabel : null,
      timeLabel: timezone ? sessionTimeLabel : null,
      dateParts: timezone ? sessionDateParts : null,
    };

    return (
      <table
        data-component="Aurelia"
        width="100%"
        cellPadding={0}
        cellSpacing={0}
        border={0}
        role="presentation"
        className={styles.email}
        style={
          {
            '--bg': bg,
            '--container': container,
            '--accent': accent,
            '--button-color': buttonColor,
            '--secondary-accent': secondaryAccent,
            '--heading': heading,
            '--text-on-dark-fill': textOnDarkFill,
            '--body': body,
            '--button-text': buttonText,
            '--heading-font': headingFont,
            '--body-font': bodyFont,
            '--image-radius': imageRadius,
            '--button-radius': buttonRadius,
            background: 'var(--bg)',
          } as React.CSSProperties
        }
      >
        <tbody>
          <tr>
            <td align="center" style={{ padding: '0 32px' }}>
              <table
                width={600}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                role="presentation"
                style={{ maxWidth: '600px', background: 'var(--container)' }}
              >
                <tbody>
                  <tr>
                    <td>
                      {blocks.map((block) => {
                        if (block.id === BLOCK_ID.description && !eventDescription) return null;

                        return (
                          <BlockShell
                            key={block.id}
                            block={block}
                            isEditMode={!!isEditMode}
                            isFirst={block.id === blocks[0]?.id}
                            isLast={block.id === blocks[blocks.length - 1]?.id}
                            selected={block.id === selectedBlockId}
                            onSelect={onSelectBlock}
                            onDelete={onDeleteBlock}
                            onMoveUp={(id) => onMoveBlock?.(id, 'up')}
                            onMoveDown={(id) => onMoveBlock?.(id, 'down')}
                          >
                            <BlockRenderer
                              block={block}
                              stylePreset={AURELIA_BLOCK_PRESETS[block.id]}
                              onValueChange={onUpdateBlockValue}
                              onImageChange={onUpdateBlockImage}
                              context={blockContext}
                            />
                          </BlockShell>
                        );
                      })}
                    </td>
                  </tr>
                </tbody>
              </table>

              <table
                width={600}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                role="presentation"
                style={{ maxWidth: '600px' }}
              >
                <tbody>
                  {!!calendarLinks && (
                    <tr>
                      <td align="center" style={{ padding: '24px 0' }}>
                        <CalendarLinksWidget links={calendarLinks} color="var(--body)" padding="0" />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <table
      data-component="Aurelia"
      width="100%"
      cellPadding={0}
      cellSpacing={0}
      border={0}
      role="presentation"
      className={styles.email}
      style={
        {
          '--bg': bg,
          '--container': container,
          '--accent': accent,
          '--button-color': buttonColor,
          '--secondary-accent': secondaryAccent,
          '--heading': heading,
          '--text-on-dark-fill': textOnDarkFill,
          '--body': body,
          '--button-text': buttonText,
          '--heading-font': headingFont,
          '--body-font': bodyFont,
          background: 'var(--bg)',
        } as React.CSSProperties
      }
    >
      <tbody>
        <tr>
          <td align="center" style={{ padding: '0 32px' }}>
            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px', background: 'var(--container)' }}
            >
              <tbody>
                <tr>
                  <td>
                    <LogoWidget src={headerImg} setImage={setHeaderImage} height={40} padding="24px" />
                  </td>
                </tr>

                <tr>
                  <td>
                    <HeaderWidget
                      value={DEFAULTS.liveBadgeText}
                      fitContent
                      color="#17181C"
                      fontFamily="var(--heading-font)"
                      fontSize="14px"
                      fontWeight={700}
                      whiteSpace="nowrap"
                      background="var(--secondary-accent)"
                      borderRadius="100px"
                      padding="8px 16px"
                      margin="0 56px 20px 56px"
                    />
                  </td>
                </tr>

                <tr>
                  <td>
                    <HeaderWidget
                      value={_salutation}
                      setValue={setSalutation}
                      color="var(--heading)"
                      fontFamily="var(--heading-font)"
                      fontSize="80px"
                      fontWeight={700}
                      lineHeight="68px"
                      whiteSpace="pre-line"
                      padding="0 56px 40px 56px"
                    />
                  </td>
                </tr>

                <tr>
                  <td>
                    <ImageWidget
                      src={bodyImg}
                      setImage={setMainImage}
                      width={600}
                      height={400}
                      borderRadius={imageRadius}
                    />
                  </td>
                </tr>

                {eventDescription && (
                  <tr>
                    <td>
                      <ParagraphWidget
                        value={eventDescription}
                        setValue={setDescription}
                        color="#FFFFFF"
                        fontFamily="var(--body-font)"
                        fontSize="24px"
                        fontWeight={700}
                        lineHeight="29px"
                        padding="48px 56px 32px 56px"
                      />
                    </td>
                  </tr>
                )}

                {sessionTimestamp && (
                  <tr>
                    <td style={{ padding: '12px 56px' }}>
                      <table
                        width="100%"
                        cellPadding={0}
                        cellSpacing={0}
                        border={0}
                        role="presentation"
                        style={{ background: '#000000', borderRadius: '4px' }}
                      >
                        <tbody>
                          <tr>
                            <td style={{ padding: '32px' }}>
                              <div
                                style={{
                                  color: 'var(--body)',
                                  opacity: 0.6,
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  marginBottom: '4px',
                                }}
                              >
                                Date
                              </div>
                              <div
                                style={{
                                  color: 'var(--text-on-dark-fill)',
                                  fontSize: '20px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 700,
                                  marginBottom: '24px',
                                }}
                              >
                                {sessionDateLabel}
                              </div>
                              <div
                                style={{
                                  color: 'var(--body)',
                                  opacity: 0.6,
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  marginBottom: '4px',
                                }}
                              >
                                {DEFAULTS.startTimeLabel}
                              </div>
                              <div
                                style={{
                                  color: 'var(--text-on-dark-fill)',
                                  fontSize: '20px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 700,
                                }}
                              >
                                {sessionTimeLabel}
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}

                <tr>
                  <td style={{ padding: '20px 56px' }}>
                    <table width="100%" cellPadding={0} cellSpacing={0} border={0} role="presentation">
                      <tbody>
                        <tr>
                          <td
                            align="center"
                            valign="middle"
                            style={{ background: 'var(--button-color)', borderRadius: buttonRadius, height: '56px' }}
                          >
                            <a
                              href={anchorHref}
                              onClick={(e) => e.preventDefault()}
                              style={{
                                textDecoration: 'none',
                                display: 'inline-block',
                                height: '100%',
                                textAlign: 'center',
                              }}
                            >
                              <ContentEditable
                                value={_cta}
                                setValue={setCta}
                                style={{
                                  color: 'var(--button-text)',
                                  fontSize: '18px',
                                  fontFamily: 'var(--heading-font)',
                                  fontWeight: 700,
                                  lineHeight: '56px',
                                  wordWrap: 'break-word',
                                  whiteSpace: 'nowrap',
                                }}
                              />
                            </a>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>

                <tr>
                  <td>
                    <div style={{ opacity: 0.8 }}>
                      <ParagraphWidget
                        value={DEFAULTS.complianceText}
                        color="#FFEAED"
                        fontFamily="var(--body-font)"
                        fontSize="11px"
                        fontWeight={400}
                        lineHeight="17px"
                        padding="56px 56px 40px 56px"
                      />
                    </div>
                    <FooterWidget
                      value={DEFAULTS.footerText}
                      src=""
                      brandliveLogo={brandliveLogo}
                      privacyPolicyLabel={privacyPolicyLabel}
                      bodyColor={bodyColor}
                      borderRadius={containerRadius}
                      accentColor={accent}
                      color={footerTextColor}
                      background={footerBackground}
                      {...FOOTER_CARD_STYLE}
                      fontFamily="var(--body-font)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px' }}
            >
              <tbody>
                {!!calendarLinks && (
                  <tr>
                    <td align="center" style={{ padding: '24px 0' }}>
                      <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                        <tbody>
                          <tr>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.iCal}
                              >
                                iCal
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--body)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.outlook}
                              >
                                Outlook
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--body)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.google}
                              >
                                Google
                              </a>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
